import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { InputAdornment, MenuItem } from '@material-ui/core';
import TextField from 'Components/Atoms/TextField';
import Checkbox from 'Components/Atoms/Checkbox';
import IconButton from 'Components/Atoms/IconButton';
import RadioButtonGroup from 'Components/Atoms/RadioButtonGroup';
import { Close } from 'Components/Atoms/Icons';
import {
  canonicalVatRate,
  centsFromPercentShares,
  isPreciseOnlyVatRate,
  percentSharesFromCents,
  redistributeVatComponents,
  VATComponent,
  vatComponentsDifferenceCents,
  vatRatesForCountry,
  VatTaxCountry,
} from 'types/shared/helper/eventVatComponents';
import './vat-split-editor.scss';

/** Rates the editors offered before the country catalog; kept when no country is known. */
const LEGACY_RATES = ['7.00', '19.00', '0.00', '10.00', '20.00'];

const euro = (cents: number) =>
  new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(cents / 100);

const parseDecimal = (text: string) => {
  const normalized = text.trim().replace(',', '.');
  return /^\d+(\.\d{0,2})?$/.test(normalized) ? Number(normalized) : null;
};

const percentText = (value: number) => String(value).replace('.', ',');

// Translation key (namespace "calendar"); TextField translates errorMessage itself.
const INVALID_AMOUNT = 'Please enter a valid amount, e.g. 29.70.';

/**
 * Stored title of the one component that keeps a single decimal rate
 * explicitly. Persisted data and the discriminator below, never translated.
 */
const SINGLE_RATE_TITLE = 'Gesamtpreis';

type Props = {
  idPrefix: string;
  /** Full effective gross unit price the split must add up to. */
  grossCents: number;
  /** How the price is made up, already translated, e.g. "Event-Grundpreis + Optionsbetrag". */
  priceHint?: string;
  vatRate: string;
  vatComponents?: VATComponent[] | null;
  country: VatTaxCountry | null;
  countryLoaded: boolean;
  /** Stored but unsupported country (e.g. "ES"), shown to explain the block. */
  storedCountry?: string;
  restaurantId: string;
  onChange: (next: { vatRate: string; vatComponents: VATComponent[] | null }) => void;
  /** Reports visible input that is not (yet) saved as amounts; Save must wait. */
  onPendingChange?: (message: string | null) => void;
};

/**
 * Tax split of one option or event add-on. Only fixed cent amounts are saved;
 * percent shares are an input aid calculated from and into those amounts.
 */
const VatSplitEditor = ({
  idPrefix,
  grossCents,
  priceHint,
  vatRate,
  vatComponents,
  country,
  countryLoaded,
  storedCountry,
  restaurantId,
  onChange,
  onPendingChange,
}: Props) => {
  const { t, i18n } = useTranslation('calendar');
  // The rate catalog carries German and English labels; French writes rates
  // like German ("8,1 %"). A label with a qualifier ("3.8 % (accommodation)")
  // is words, not only a number: it comes from the UI catalogs per language.
  const labelLanguage = i18n.language === 'en' ? 'en' : 'de';
  const rateLabel = (entry: { label: { de: string; en: string } }) =>
    entry.label.en.includes('(') ? t(`calendar_${entry.label.en}`) : entry.label[labelLanguage];
  const [inputMode, setInputMode] = useState<'euro' | 'percent'>('euro');
  const [euroDrafts, setEuroDrafts] = useState<Record<string, string>>({});
  const [percentDrafts, setPercentDrafts] = useState<Record<string, string> | null>(null);

  const components = vatComponents ?? [];
  // One component at a decimal rate is the explicit form of a single rate
  // (e.g. 8,1 %): shown as a single rate, not as a split.
  const singleDecimal =
    components.length === 1 &&
    isPreciseOnlyVatRate(components[0].vatRate) &&
    components[0].title === SINGLE_RATE_TITLE;
  const split = vatComponents !== undefined && vatComponents !== null && !singleDecimal;
  const catalog = vatRatesForCountry(country);
  const rateOptions = catalog.length
    ? catalog.map((entry) => ({ value: entry.rate, label: rateLabel(entry) }))
    : LEGACY_RATES.map((rate) => ({ value: rate, label: `${Number(rate)} %` }));
  const currentRate = singleDecimal ? components[0].vatRate : canonicalVatRate(vatRate) ?? vatRate;
  const scalarOptions = rateOptions.some((option) => option.value === currentRate)
    ? rateOptions
    : [
        ...rateOptions,
        { value: currentRate, label: t('calendar_{{rate}} % (previous value)', { rate: Number(currentRate) }) },
      ];
  const difference = vatComponentsDifferenceCents(components, grossCents);
  const splitTotal = grossCents - difference;

  const invalidEuro = (component: VATComponent) =>
    inputMode === 'euro' &&
    euroDrafts[component.id] !== undefined &&
    parseDecimal(euroDrafts[component.id]) === null;

  // Validation looks only at an explicit percent draft; without one the stored
  // cents apply and nothing blocks saving.
  const percentSum = percentDrafts
    ? components.reduce((total, c) => total + (parseDecimal(percentDrafts[c.id] ?? '') ?? NaN), 0)
    : 100;
  const percentInvalid = split && inputMode === 'percent' && Math.round(percentSum * 100) !== 10000;
  const percentDraftComplete = !!percentDrafts && Math.round(percentSum * 100) === 10000;
  // Display: without a draft, the sum of the rounded shares actually shown
  // (0 % for a free price), never a fixed 100 %.
  const shownPercentSum = percentDrafts
    ? percentSum
    : percentSharesFromCents(grossCents, components).reduce((total, share) => total + share, 0);
  const percentSumText = Number.isFinite(shownPercentSum)
    ? percentText(Math.round(shownPercentSum * 100) / 100)
    : '?';
  const pendingMessage = !split
    ? null
    : components.some(invalidEuro)
      ? t(`calendar_${INVALID_AMOUNT}`)
      : percentInvalid
        ? t(
            'calendar_The shares add up to {{percent}} % – they must total 100 %. Until then, the previous amounts stay saved.',
            { percent: percentSumText }
          )
        : null;
  const pendingId = `${idPrefix}-split-pending`;

  useEffect(() => {
    onPendingChange?.(pendingMessage);
  }, [pendingMessage]);

  // A single rate covers the whole price, also after a price change.
  useEffect(() => {
    if (singleDecimal && components[0].grossAmountCents !== Math.max(grossCents, 0)) {
      emit([{ ...components[0], grossAmountCents: Math.max(grossCents, 0) }], components[0].vatRate);
    }
  }, [singleDecimal, grossCents]);

  // A completed percent entry is already stored as cents; after a price change
  // its shares are shown of the new price. An unfinished entry stays as typed.
  useEffect(() => {
    if (percentDraftComplete) setPercentDrafts(null);
  }, [grossCents]);

  const emit = (next: VATComponent[] | null, rate = vatRate) =>
    onChange({ vatRate: rate, vatComponents: next });

  const resetDrafts = () => {
    setEuroDrafts({});
    setPercentDrafts(null);
  };

  const startSplit = () => {
    const standard = catalog.find((entry) => entry.kind === 'standard')?.rate ?? currentRate;
    const firstRate = catalog.some((entry) => entry.rate === currentRate) ? currentRate : standard;
    emit([
      { id: `${Date.now()}-1`, title: '', grossAmountCents: Math.max(grossCents, 0), vatRate: firstRate },
      { id: `${Date.now()}-2`, title: '', grossAmountCents: 0, vatRate: standard },
    ]);
    resetDrafts();
  };

  // A decimal rate (e.g. 8,1 %) is saved explicitly as a one-component split,
  // so a bare `vatRate` never silently changes the historical tax path.
  const chooseSingleRate = (rate: string) =>
    isPreciseOnlyVatRate(rate)
      ? onChange({
          vatRate: rate,
          vatComponents: [{ id: `${Date.now()}`, title: SINGLE_RATE_TITLE, grossAmountCents: Math.max(grossCents, 0), vatRate: rate }],
        })
      : onChange({ vatRate: rate, vatComponents: null });

  // Leaving the split keeps the first rate as the single rate; a decimal one
  // stays explicit, so switching off never produces an unsaveable state.
  const endSplit = () => {
    resetDrafts();
    chooseSingleRate(components[0]?.vatRate ?? currentRate);
  };

  const updateComponent = (id: string, patch: Partial<VATComponent>) =>
    emit(components.map((component) => (component.id === id ? { ...component, ...patch } : component)));

  const percentShown = (component: VATComponent, index: number) => {
    if (percentDrafts && percentDrafts[component.id] !== undefined) return percentDrafts[component.id];
    return percentText(percentSharesFromCents(grossCents, components)[index]);
  };

  const changePercent = (id: string, text: string) => {
    const drafts = {
      ...Object.fromEntries(components.map((c, i) => [c.id, percentShown(c, i)])),
      [id]: text,
    };
    setPercentDrafts(drafts);
    const shares = components.map((c) => parseDecimal(drafts[c.id]));
    if (shares.some((share) => share === null)) return;
    const cents = centsFromPercentShares(grossCents, shares as number[]);
    if (cents) emit(components.map((component, i) => ({ ...component, grossAmountCents: cents[i] })));
  };

  const euroShown = (component: VATComponent) => {
    const draft = euroDrafts[component.id];
    const parsed = draft === undefined ? null : parseDecimal(draft);
    return draft !== undefined && (parsed === null || Math.round(parsed * 100) === component.grossAmountCents)
      ? draft
      : (component.grossAmountCents / 100).toFixed(2).replace('.', ',');
  };

  const changeEuro = (id: string, text: string) => {
    setEuroDrafts((drafts) => ({ ...drafts, [id]: text }));
    const value = parseDecimal(text);
    if (value !== null) updateComponent(id, { grossAmountCents: Math.round(value * 100) });
  };

  return (
    <div className="vat-split">
      {!split && (
        <TextField
          id={`${idPrefix}-vat`}
          select
          fullWidth
          label="VAT rate"
          labelTranslation="calendar"
          value={currentRate}
          onChange={(event) => chooseSingleRate(event.target.value)}
        >
          {scalarOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      )}

      <div className="vat-split__header">
        <Checkbox
          className="vat-split__toggle"
          color="primary"
          label="Split the price across several VAT rates"
          translation="calendar"
          labelColor="textPrimary"
          checked={split}
          onChange={(event) => {
            if (event.target.checked) startSplit();
            else endSplit();
          }}
        />
        {split && (
          <p className="vat-split__price">
            {t('calendar_Total price:')} <strong>{euro(grossCents)}</strong>
            {priceHint ? ` (${priceHint})` : ''}
            {country
              ? ` · ${t('calendar_VAT rates for {{country}}', { country: t(`calendar_Country ${country}`) })}`
              : ''}
          </p>
        )}
      </div>

      {split && (
        <>
          {countryLoaded && !country && (
            <p className="vat-split__notice" role="alert">
              {storedCountry
                ? t('calendar_No selectable VAT rates exist for the stored country “{{country}}”.', {
                    country: storedCountry,
                  })
                : t("calendar_A VAT split requires the restaurant's country to be set.")}{' '}
              <a
                href={`/${restaurantId}/settings/general/general-information`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('calendar_Set the country in the general information')}
              </a>
            </p>
          )}

          <div className="vat-split__mode">
            <RadioButtonGroup
              isRow
              name={`${idPrefix}-split-mode`}
              value={inputMode}
              options={[
                { value: 'euro', label: 'Amounts in €', translation: 'calendar' },
                { value: 'percent', label: 'Shares in %', translation: 'calendar' },
              ]}
              onChange={(_event, value) => {
                setInputMode(value === 'percent' ? 'percent' : 'euro');
                resetDrafts();
              }}
            />
            {inputMode === 'percent' && (
              <p className="vat-split__hint">
                {t('calendar_Shares are saved as fixed euro amounts and are not adjusted when the price changes.')}
              </p>
            )}
          </div>

          <ul className="vat-split__rows" aria-label={t('calendar_Components')}>
            {components.map((component, index) => (
              <li key={component.id} className="vat-split__row">
                <TextField
                  id={`${idPrefix}-title-${index}`}
                  label="Component name"
                  labelTranslation="calendar"
                  value={component.title}
                  onChange={(event) => updateComponent(component.id, { title: event.target.value })}
                />
                {inputMode === 'euro' ? (
                  <TextField
                    id={`${idPrefix}-amount-${index}`}
                    label="Gross amount"
                    labelTranslation="calendar"
                    helperTextTranslation="calendar"
                    inputProps={{ inputMode: 'decimal' }}
                    value={euroShown(component)}
                    errorMessage={invalidEuro(component) ? INVALID_AMOUNT : ''}
                    onChange={(event) => changeEuro(component.id, event.target.value)}
                    InputProps={{ endAdornment: <InputAdornment position="end">€</InputAdornment> }}
                  />
                ) : (
                  <TextField
                    id={`${idPrefix}-percent-${index}`}
                    label="Share of the price"
                    labelTranslation="calendar"
                    inputProps={{
                      inputMode: 'decimal',
                      'aria-invalid': percentInvalid,
                      'aria-describedby': percentInvalid ? pendingId : undefined,
                    }}
                    value={percentShown(component, index)}
                    onChange={(event) => changePercent(component.id, event.target.value)}
                    helperText={
                      percentInvalid
                        ? t('calendar_previously {{amount}}', { amount: euro(component.grossAmountCents) })
                        : euro(component.grossAmountCents)
                    }
                    InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                  />
                )}
                <TextField
                  id={`${idPrefix}-rate-${index}`}
                  select
                  label="VAT rate"
                  labelTranslation="calendar"
                  disabled={!country}
                  value={catalog.some((entry) => entry.rate === component.vatRate) ? component.vatRate : ''}
                  onChange={(event) => updateComponent(component.id, { vatRate: event.target.value })}
                >
                  {catalog.map((entry) => (
                    <MenuItem key={entry.rate} value={entry.rate}>
                      {rateLabel(entry)}
                    </MenuItem>
                  ))}
                </TextField>
                <IconButton
                  size="small"
                  color="subdued"
                  aria-label={t('calendar_Remove component {{name}}', { name: component.title || index + 1 })}
                  disabled={components.length <= 1}
                  onClick={() => {
                    emit(components.filter((c) => c.id !== component.id));
                    resetDrafts();
                  }}
                >
                  <Close fontSize="small" />
                </IconButton>
              </li>
            ))}
          </ul>

          {percentInvalid && (
            <p id={pendingId} className="vat-split__pending" role="alert">
              {pendingMessage}
            </p>
          )}

          <button
            type="button"
            className="vat-split__add"
            onClick={() => {
              emit([
                ...components,
                {
                  id: `${Date.now()}`,
                  title: '',
                  grossAmountCents: 0,
                  vatRate: catalog.find((entry) => entry.kind === 'standard')?.rate ?? currentRate,
                },
              ]);
              resetDrafts();
            }}
          >
            {t('calendar_Add component')}
          </button>

          <div className="vat-split__summary" role="status" aria-live="polite">
            {inputMode === 'percent' && (
              <span className={percentInvalid ? 'vat-split__difference' : undefined}>
                {t('calendar_Shares total: {{percent}} %', { percent: percentSumText })}
              </span>
            )}
            <span>{t('calendar_Split: {{amount}}', { amount: euro(splitTotal) })}</span>
            {difference !== 0 && (
              <span className="vat-split__difference">
                {difference > 0
                  ? t('calendar_Still to split: {{amount}}', { amount: euro(difference) })
                  : t('calendar_{{amount}} split too much', { amount: euro(-difference) })}
              </span>
            )}
            {difference !== 0 && splitTotal > 0 && grossCents >= 0 && (
              <button
                type="button"
                className="vat-split__redistribute"
                onClick={() => {
                  emit(redistributeVatComponents(components, grossCents));
                  resetDrafts();
                }}
              >
                {t('calendar_Redistribute proportionally')}
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default VatSplitEditor;
